import React, { useState, useEffect } from 'react';
import { PageHeader, StatCard, Card, Badge } from '../../components/UI';
import { adminApi } from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/api/admin/dashboard');
      if (data.success) { setStats(data.stats); setPending(data.pendingPayments || []); setRenewals(data.upcomingRenewals || []); }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!window.confirm('Approve this payment and activate account?')) return;
    const { data } = await adminApi.put(`/api/admin/payments/${id}/approve`, {});
    if (data.success) { toast.success('Payment approved! Account activated.'); load(); }
    else toast.error(data.message);
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    const { data } = await adminApi.put(`/api/admin/payments/${id}/reject`, { reason });
    if (data.success) { toast.success('Payment rejected.'); load(); }
    else toast.error(data.message);
  };

  return (
    <div>
      <PageHeader title="Admin Dashboard" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon="👥" value={stats?.totalUsers ?? '—'} label="Total Users" />
        <StatCard icon="✅" value={stats?.activeUsers ?? '—'} label="Active Users" />
        <StatCard icon="⏳" value={stats?.pendingApprovals ?? '—'} label="Pending Approvals" color="#ffd700" />
        <StatCard icon="💰" value={stats?.revenueToday ? `₹${parseFloat(stats.revenueToday).toLocaleString()}` : '—'} label="Revenue Today" />
        <StatCard icon="📅" value={stats?.revenueThisMonth ? `₹${parseFloat(stats.revenueThisMonth).toLocaleString()}` : '—'} label="This Month" />
        <StatCard icon="🚫" value={stats?.suspendedAccounts ?? '—'} label="Suspended" color="#ff6b6b" />
      </div>

      {/* Pending Payments */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>⏳ Pending Payment Approvals</div>
        {pending.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>No pending payments ✅</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['Client', 'Plan', 'Amount', 'Txn ID', 'Date', 'Actions'].map(h => <th key={h} style={{ background: 'var(--bg)', padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)' }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {pending.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px 14px' }}><div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.email}</div></td>
                    <td style={{ padding: '12px 14px' }}><Badge>{p.plan_name}</Badge></td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--green)', fontSize: 13 }}>{p.currency === 'USD' ? '$' : '₹'}{parseFloat(p.total_amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 11, color: '#4da8ff' }}>{p.transaction_id || '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12 }}>{new Date(p.created_at).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => approve(p.id)} style={{ background: 'rgba(0,231,76,0.1)', color: 'var(--green)', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>✅ Approve</button>
                        <button onClick={() => reject(p.id)} style={{ background: 'rgba(255,68,68,0.1)', color: '#ff6b6b', border: 'none', padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>❌ Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upcoming Renewals */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card title="📅 Upcoming Renewals (7 days)">
          {renewals.length === 0 ? <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20, fontSize: 13 }}>No renewals in next 7 days</div>
            : renewals.map(u => (
              <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <div><div style={{ fontWeight: 600 }}>{u.name}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.plan_name}</div></div>
                <span style={{ color: '#ffd700', fontSize: 12 }}>{new Date(u.plan_expiry_date).toLocaleDateString('en-IN')}</span>
              </div>
            ))}
        </Card>
        <Card title="📊 Quick Stats">
          {stats ? [['Active Users', stats.activeUsers, 'var(--green)'], ['Pending Approvals', stats.pendingApprovals, '#ffd700'], ['Suspended', stats.suspendedAccounts, '#ff6b6b'], ['Password Reset Requests', stats.passwordResets || 0, '#4da8ff']].map(([label, val, color]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}</span>
              <span style={{ fontWeight: 700, color }}>{val}</span>
            </div>
          )) : null}
        </Card>
      </div>
    </div>
  );
}